/**
 * Admin Permission Utilities
 * Safely check if current admin user has access to specific sections
 */

import { useState, useEffect } from 'react';
import { getValidToken, API_URL } from './authUtils';

// Cache for admin permissions to avoid repeated API calls
let permissionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get current admin user permissions from backend
 */
export const fetchAdminPermissions = async () => {
  try {
    const token = getValidToken();
    if (!token) {
      return null;
    }

    // Check cache first
    const now = Date.now();
    if (permissionsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      return permissionsCache;
    }

    const response = await fetch(`${API_URL}/api/admin/me`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      const adminUser = await response.json();
      permissionsCache = adminUser;
      cacheTimestamp = now;
      return adminUser;
    } else {
      const errorText = await response.text();
      console.warn('Admin permissions API call failed:', response.status, errorText);
      // If endpoint doesn't exist yet, assume full permissions (fallback)
      return null;
    }
  } catch (error) {
    console.warn('Could not fetch admin permissions, assuming full access:', error);
    // Graceful fallback - if permission system fails, show everything
    return null;
  }
};

/**
 * Clear the permissions cache (call when admin user changes)
 */
export const clearPermissionsCache = () => {
  permissionsCache = null;
  cacheTimestamp = null;
};

/**
 * Check if a section should be visible to the current admin
 * @param {string} sectionName - The section to check (e.g., 'recruiter_management')
 * @param {Object} permissions - Optional: pass permissions object to avoid fetching
 */
export const canAccessSection = async (sectionName, permissions = null) => {
  try {
    const adminPerms = permissions || await fetchAdminPermissions();

    // If no permissions found, assume full access (safe fallback)
    if (!adminPerms) {
      return true;
    }

    // Convert section name to permission field name
    const permissionField = `hide_${sectionName}`;

    // If permission field exists and is true, section is hidden
    if (adminPerms.hasOwnProperty(permissionField)) {
      return !adminPerms[permissionField]; // Return opposite of hide flag
    }

    // If permission field doesn't exist, allow access
    return true;
  } catch (error) {
    console.warn(`Error checking access for ${sectionName}:`, error);
    // Safe fallback - show section if permission check fails
    return true;
  }
};

/**
 * Synchronous version - check if a section should be visible (for React render)
 * @param {string} sectionName - The section to check (e.g., 'recruiter_management')
 * @param {Object} permissions - Pass permissions object from useAdminPermissions hook
 */
export const canAccessSectionSync = (sectionName, permissions = null) => {
  try {
    // If no permissions found, assume full access (safe fallback)
    if (!permissions) {
      return true;
    }

    // Convert section name to permission field name
    const permissionField = `hide_${sectionName}`;

    // If permission field exists and is true, section is hidden
    if (permissions.hasOwnProperty(permissionField)) {
      return !permissions[permissionField]; // Return opposite of hide flag
    }

    // If permission field doesn't exist, allow access
    return true;
  } catch (error) {
    console.warn(`Error checking access for ${sectionName}:`, error);
    // Safe fallback - show section if permission check fails
    return true;
  }
};

/**
 * Check multiple sections at once
 * @param {Array<string>} sections - Array of section names to check
 * @param {Object} permissions - Optional: pass permissions object
 * @returns {Object} - Object with section names as keys and access boolean as values
 */
export const canAccessSections = async (sections, permissions = null) => {
  try {
    const adminPerms = permissions || await fetchAdminPermissions();
    const result = {};

    for (const section of sections) {
      result[section] = await canAccessSection(section, adminPerms);
    }

    return result;
  } catch (error) {
    console.warn('Error checking multiple sections:', error);
    // Safe fallback - allow all sections
    const result = {};
    sections.forEach(section => {
      result[section] = true;
    });
    return result;
  }
};

/**
 * Hook for React components to get admin permissions
 * @returns {Object|null} - Admin user permissions object or null
 */
export const useAdminPermissions = () => {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      // Only try to load permissions if we have a valid token
      const token = getValidToken();
      if (!token) {
        setLoading(false);
        setPermissions(null);
        return;
      }

      setLoading(true);
      const perms = await fetchAdminPermissions();
      setPermissions(perms);
      setLoading(false);
    };

    loadPermissions();
  }, []);

  return { permissions, loading };
};


/**
 * Available section names for permission checking
 */
export const ADMIN_SECTIONS = {
  RECRUITER_MANAGEMENT: 'recruiter_management',
  STUDENT_MANAGEMENT: 'student_management',
  STUDENT_RECORDS: 'student_records',
  PARTNER_MANAGEMENT: 'partner_management',
  TOKEN_MANAGEMENT: 'token_management',
  USER_ACTIVITY: 'user_activity',
  COMPANY_MANAGEMENT: 'company_management',
  ALUMNI_MANAGEMENT: 'alumni_management',
  MAP_VIEW_ACCESS: 'map_view',
  BLOG_MANAGEMENT: 'blog_management',
  JOB_ANALYTICS_ACCESS: 'job_analytics',
  CONTENT_CURATION: 'content_curation',
  CURATOR_MANAGEMENT: 'curator_management',
  FEATURE_CONTROL_ACCESS: 'feature_control',
  BUSINESS_DEVELOPMENT: 'business_development',
  VERIFICATION_DASHBOARD: 'verification_dashboard',
  SYSTEM_SETTINGS: 'system_settings',
  PLATFORM_SETTINGS: 'platform_settings',
  MEMBERSHIP_MANAGER: 'membership_manager',
  CRM: 'crm',
  // Fine-grained token permissions
  RECRUITER_TOKENS: 'recruiter_tokens',
  STUDENT_TOKENS: 'student_tokens',
  PARTNER_TOKENS: 'partner_tokens',
  // Fine-grained activity permissions
  RECRUITER_ACTIVITY: 'recruiter_activity',
  STUDENT_ACTIVITY: 'student_activity',
  PARTNER_ACTIVITY: 'partner_activity',
  // Executive Intelligence (page-level)
  EXECUTIVE_INTELLIGENCE: 'executive_intelligence',
  // Executive Intelligence tab permissions
  EXEC_DASHBOARD: 'exec_dashboard',
  EXEC_SEARCH: 'exec_search',
  EXEC_MAP: 'exec_map',
  EXEC_FAVORITES: 'exec_favorites',
  EXEC_IMPORTS: 'exec_imports',
  EXEC_REVIEWS: 'exec_reviews',
  EXEC_SCORING: 'exec_scoring',
  EXEC_CALIBRATION: 'exec_calibration',
  EXEC_REPORTS: 'exec_reports',
  EXEC_ANALYTICS: 'exec_analytics',
  EXEC_BROKERAGE: 'exec_brokerage',
  // SWEE (Strategic Workforce Evolution Engine)
  SWEE: 'swee',
  // Alumni Dash Admin
  ALUMNI_DASH_ADMIN: 'alumni_dash_admin',
  // Grant Management
  GRANT_MANAGEMENT: 'grant_management'
};
